'use client';
import type { Placement } from '@floating-ui/react-dom';
import { computePosition, flip, shift } from '@floating-ui/react-dom';
import { type RefObject, useCallback, useEffect, useRef } from 'react';

export interface DropdownElements {
	target: HTMLElement;
	popover: HTMLElement;
	anchor: HTMLElement;
}

export interface DropdownHookProps {
	mode?: 'click' | 'hover';
	targetRef: RefObject<HTMLElement | null>;
	anchorRef?: RefObject<HTMLElement | null>;
	placement?: Placement;
	onChange?(showed: boolean, elements: DropdownElements): void;
}

type ClickArea = 'outside' | 'on-trigger' | 'inside-close' | 'inside-keep';
// outside — клик вне таргета и поповера
// on-trigger — клик по таргету
// inside-close — клик внутри поповера по элементу с data-coc (close on click)
// inside-keep — клик внутри поповера по элементу без data-coc

function classifyClickArea(
	target: HTMLElement | null,
	popover: HTMLElement | null,
	event: MouseEvent
): ClickArea {
	if (!target || !popover) {
		return 'outside';
	}

	const path = event.composedPath();

	if (path.includes(target)) {
		return 'on-trigger';
	}

	if (path.includes(popover)) {
		for (let i = path.indexOf(popover) - 1, element: HTMLElement; i >= 0; i--) {
			element = path[i] as HTMLElement;

			if (element.dataset.coc) {
				return 'inside-close';
			}
		}

		return 'inside-keep';
	}

	return 'outside';
}

export function useDropdown({
	mode = 'click',
	targetRef,
	anchorRef = targetRef,
	placement,
	onChange,
}: DropdownHookProps) {
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const placementRef = useRef<Placement>(placement);
	const onChangeRef = useRef(onChange);
	const getElements = useCallback(
		(): DropdownElements => ({
			target: targetRef.current as HTMLElement,
			popover: popoverRef.current as HTMLDivElement,
			anchor: anchorRef.current as HTMLElement,
		}),
		[targetRef, anchorRef]
	);

	onChangeRef.current = onChange;
	placementRef.current = placement;

	useEffect(() => {
		const target = targetRef.current;
		const popover = popoverRef.current;
		const anchor = anchorRef.current;
		let opened = false;

		if (!target || !popover || !anchor) {
			return;
		}

		const updatePosition = () => {
			if (!opened || !popover || !anchor) {
				return;
			}

			void computePosition(anchor, popover, {
				placement: placementRef.current,
				middleware: [flip(), shift({ padding: 5 })],
				strategy: 'fixed',
			}).then(({ x, y }) => {
				Object.assign(popover.style, {
					position: 'fixed',
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		};

		const showPopover = () => {
			if (popover && !opened) {
				opened = true;
				popover.showPopover();
				try {
					updatePosition();
					onChangeRef.current?.(true, getElements());
				} catch (error) {
					popover.showPopover();
					onChangeRef.current?.(true, getElements());
				}
			}
		};

		const hidePopover = () => {
			if (popover && opened) {
				opened = false;
				popover.hidePopover();
				onChangeRef.current?.(false, getElements());
			}
		};

		const togglePopover = () => {
			if (opened) {
				hidePopover();
			} else {
				showPopover();
			}
		};

		let timeout: ReturnType<typeof setTimeout> | null = null;

		const debouncedShowPopover = () => {
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}

			showPopover();
		};

		const debouncedHidePopover = () => {
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}

			timeout = setTimeout(() => {
				hidePopover();
			}, 300);
		};

		const onDocumentClick = (event: MouseEvent) => {
			if (!opened) {
				return;
			}

			const area = classifyClickArea(target, popover, event);

			switch (area) {
				case 'outside':
					hidePopover();

					if (navigator.maxTouchPoints > 0) {
						event.stopPropagation();
						event.preventDefault();
					}

					break;
				case 'inside-close':
					hidePopover();
					break;
				case 'inside-keep':
				case 'on-trigger':
				default:
					break;
			}
		};

		const onScroll = () => {
			if (opened) {
				updatePosition();
			}
		};

		if (mode === 'click') {
			target.addEventListener('click', togglePopover);
		} else {
			target.addEventListener('mouseenter', debouncedShowPopover);
			popover.addEventListener('mouseenter', debouncedShowPopover);
			target.addEventListener('mouseleave', debouncedHidePopover);
			popover.addEventListener('mouseleave', debouncedHidePopover);

			target.addEventListener('focus', debouncedShowPopover);
			popover.addEventListener('focus', debouncedShowPopover, true);
			target.addEventListener('blur', debouncedHidePopover);
			popover.addEventListener('blur', debouncedHidePopover, true);
		}

		document.addEventListener('click', onDocumentClick, true);
		window.addEventListener('scroll', onScroll, true);

		return () => {
			if (mode === 'click') {
				target.removeEventListener('click', togglePopover);
			} else {
				target.removeEventListener('mouseenter', debouncedShowPopover);
				popover.removeEventListener('mouseenter', debouncedShowPopover);
				target.removeEventListener('mouseleave', debouncedHidePopover);
				popover.removeEventListener('mouseleave', debouncedHidePopover);

				target.removeEventListener('focus', debouncedShowPopover);
				popover.removeEventListener('focus', debouncedShowPopover, true);
				target.removeEventListener('blur', debouncedHidePopover);
				popover.removeEventListener('blur', debouncedHidePopover, true);
			}

			document.removeEventListener('click', onDocumentClick, true);
			window.removeEventListener('scroll', onScroll, true);

			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [mode, targetRef, anchorRef, getElements]);

	return popoverRef;
}
